import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import Logo from '../components/Logo';
import api from '../api/client';
import { useAuthStore } from '../store/authStore';
import FormField, { validateRequired } from '../components/ui/FormField';
import useFormSubmit from '../hooks/useFormSubmit';
import { useCompanyPath } from '../utils/routing';

const initial = {
  company_name: '', gstin: '', pan: '', address: '', city: '', state: '', pincode: '',
  phone: '', email: '', currency: 'INR', fiscal_year_start: '04-01', warehouse_name: 'Main Warehouse',
};

export default function SetupPage() {
  const [status, setStatus] = useState<any>(null);
  const [form, setForm] = useState(initial);
  const { submit, submitting } = useFormSubmit();
  const { isAuthenticated, user } = useAuthStore();
  const navigate = useNavigate();
  const modulesPath = useCompanyPath('/modules');
  const loginPath = useCompanyPath('/login');

  useEffect(() => {
    api.get('/setup/status').then(r => {
      setStatus(r.data);
      setForm(f => ({ ...f, company_name: r.data.company_name || '' }));
    }).catch(() => setStatus({ configured: false, has_admin: true }));
  }, []);

  const submitForm = async (e: React.FormEvent) => {
    e.preventDefault();
    const err = validateRequired(
      { company_name: form.company_name, warehouse_name: form.warehouse_name },
      { company_name: 'Company name', warehouse_name: 'Primary warehouse' }
    );
    if (err) return toast.error(err);
    await submit(async () => {
      await api.post('/setup/complete', form);
    }, {
      successMessage: 'Company setup completed',
      errorMessage: 'Setup could not be completed',
      onSuccess: () => navigate(modulesPath),
    });
  };

  if (!status) return <div className="min-h-screen grid place-items-center text-content-secondary">Checking setup…</div>;
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-surface-muted grid place-items-center p-6">
        <div className="max-w-lg w-full card rounded-3xl shadow-xl p-8">
          <Logo size="md" />
          <h1 className="page-title mt-8">Initial company setup</h1>
          <p className="text-content-secondary mt-2">Sign in as the system administrator to configure the company, head office, warehouse and starter tax masters.</p>
          {status.configured && <div className="mt-5 rounded-xl bg-green-50 text-green-700 p-3 text-sm">Setup is already complete for {status.company_name}.</div>}
          <Link to={loginPath} state={{ from: '/setup' }} className="mt-6 block text-center gradient-bg text-white rounded-xl py-3 font-semibold">
            Administrator sign in
          </Link>
          <Link to="/" className="mt-3 block text-center text-sm text-content-secondary">Back to home</Link>
        </div>
      </div>
    );
  }
  if (!user?.is_superuser) {
    return <div className="min-h-screen grid place-items-center text-content-secondary">Only a system administrator can change initial setup.</div>;
  }

  const input = 'input-field';
  return (
    <div className="min-h-screen bg-surface-muted p-6">
      <div className="max-w-4xl mx-auto">
        <div className="flex justify-between items-center mb-6"><Logo size="md" /><Link to={modulesPath} className="text-sm text-primary-600">Skip to modules</Link></div>
        <form onSubmit={submitForm} className="card rounded-3xl p-8">
          <h1 className="page-title">Initial setup</h1>
          <p className="text-content-secondary text-sm mt-1 mb-7">This creates the operational foundation used by inventory, tax, sales and procurement.</p>
          <div className="grid sm:grid-cols-2 gap-4">
            <FormField label="Company name" required>
              <input value={form.company_name} onChange={e=>setForm({...form,company_name:e.target.value})} className={input} />
            </FormField>
            <FormField label="GSTIN"><input value={form.gstin} onChange={e=>setForm({...form,gstin:e.target.value})} className={input} /></FormField>
            <FormField label="PAN"><input value={form.pan} onChange={e=>setForm({...form,pan:e.target.value})} className={input} /></FormField>
            <FormField label="Business email"><input value={form.email} onChange={e=>setForm({...form,email:e.target.value})} className={input} /></FormField>
            <FormField label="Phone"><input value={form.phone} onChange={e=>setForm({...form,phone:e.target.value})} className={input} /></FormField>
            <FormField label="City"><input value={form.city} onChange={e=>setForm({...form,city:e.target.value})} className={input} /></FormField>
            <FormField label="State"><input value={form.state} onChange={e=>setForm({...form,state:e.target.value})} className={input} /></FormField>
            <FormField label="PIN code"><input value={form.pincode} onChange={e=>setForm({...form,pincode:e.target.value})} className={input} /></FormField>
            <FormField label="Primary warehouse" required>
              <input value={form.warehouse_name} onChange={e=>setForm({...form,warehouse_name:e.target.value})} className={input} />
            </FormField>
            <FormField label="Currency">
              <select value={form.currency} onChange={e=>setForm({...form,currency:e.target.value})} className={input}>
                <option value="INR">INR – Indian Rupee</option><option value="USD">USD – US Dollar</option>
              </select>
            </FormField>
            <FormField label="Registered address" className="sm:col-span-2">
              <textarea value={form.address} onChange={e=>setForm({...form,address:e.target.value})} className={input} rows={3} />
            </FormField>
          </div>
          <button type="submit" disabled={submitting} className="mt-7 px-6 py-3 gradient-bg text-white rounded-xl font-semibold disabled:opacity-60">
            {submitting ? 'Saving setup…' : status.configured ? 'Update company setup' : 'Complete setup'}
          </button>
        </form>
      </div>
    </div>
  );
}
